import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useQueryClient } from '@tanstack/react-query';
import {
  BoardSchema,
  TaskDetailSchema,
  TaskSummarySchema,
  type BoardDto,
  type ProjectWorkspaceDto,
  type TaskPriority,
  type TaskSummaryDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { errorMessage, patchJson, postJson } from '../../../shared/api/client';
import { businessToday } from '../../../shared/documents/dates';
import { useSession } from '../../../shared/session/session';
import { FormDialog } from '../../../shared/ui/forms';
import { ErrorState, LoadingState, PageHeader, Section } from '../../../shared/ui/page';
import {
  columnTasks,
  PRIORITY_LABELS,
  ROLE_LABELS,
  useActivity,
  useCsrf,
  useTasks,
  useWorkMutation,
  useWorkspace,
  WORK,
  workKeys,
} from './api';
import { Kanban, type MoveTarget } from './Kanban';
import { ManageDialog } from './ManageDialog';
import { TaskPanel } from './TaskPanel';
import { monthOf, TaskCalendar, TaskList } from './TaskViews';

type View = 'kanban' | 'list' | 'calendar';
const VIEWS: readonly View[] = ['kanban', 'list', 'calendar'];

/** The position a moved task takes between its new neighbours, as the API places it. */
export function positionBetween(
  column: readonly TaskSummaryDto[],
  beforeTaskId: string | null,
): number {
  if (column.length === 0) return 1024;
  if (beforeTaskId === null) return (column[column.length - 1]?.position ?? 0) + 1024;
  const index = column.findIndex((t) => t.id === beforeTaskId);
  const next = column[index]?.position ?? 0;
  const previous = index > 0 ? (column[index - 1]?.position ?? next - 2048) : next - 2048;
  return (previous + next) / 2;
}

/** Whether a task passes the filters chosen on the page. */
export function matches(
  task: TaskSummaryDto,
  filters: { q: string; assignee: string; label: string; priority: string; milestone: string },
): boolean {
  if (filters.q !== '') {
    const text = `${task.key} ${task.title}`.toLowerCase();
    if (!text.includes(filters.q.toLowerCase())) return false;
  }
  if (filters.assignee !== '' && !task.assigneeIds.includes(filters.assignee)) return false;
  if (filters.label !== '' && !task.labelIds.includes(filters.label)) return false;
  if (filters.priority !== '' && task.priority !== filters.priority) return false;
  if (filters.milestone !== '' && task.milestoneId !== filters.milestone) return false;
  return true;
}

/**
 * A project's work (`/projects/:id/work`; CLR-66): its boards as a Kanban board, a list or a
 * calendar, with filters, a task panel opened with `?task=ID`, the project's activity, and the
 * management of members, boards, milestones and labels for its managers. The board, the view, the
 * month and the filters are in the address, so a link opens the same picture.
 */
export function ProjectWorkPage() {
  const { id: projectId = '' } = useParams();
  const workspace = useWorkspace(projectId);
  const tasks = useTasks(projectId);
  const back = (
    <Button component={RouterLink} to="/work" startIcon={<ArrowBackIcon />}>
      Project work
    </Button>
  );
  if (workspace.isPending) return <LoadingState label="Loading the project's work" />;
  if (workspace.isError) {
    return (
      <Stack spacing={2}>
        <Box>{back}</Box>
        <ErrorState message={errorMessage(workspace.error)} />
      </Stack>
    );
  }
  return (
    <Workspace
      projectId={projectId}
      workspace={workspace.data}
      tasks={tasks.data ?? []}
      tasksLoading={tasks.isPending}
      tasksError={tasks.isError ? errorMessage(tasks.error) : null}
      back={back}
    />
  );
}

function Workspace({
  projectId,
  workspace,
  tasks,
  tasksLoading,
  tasksError,
  back,
}: {
  projectId: string;
  workspace: ProjectWorkspaceDto;
  tasks: readonly TaskSummaryDto[];
  tasksLoading: boolean;
  tasksError: string | null;
  back: ReactNode;
}) {
  const session = useSession();
  const csrf = useCsrf();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState<'board' | 'manage' | 'activity' | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');

  const boards = workspace.boards.filter((b) => b.status === 'active');
  const boardId = params.get('board');
  const board: BoardDto | undefined = boards.find((b) => b.id === boardId) ?? boards[0];
  const viewParam = params.get('view') as View | null;
  const view: View = viewParam !== null && VIEWS.includes(viewParam) ? viewParam : 'kanban';
  const month = params.get('month') ?? monthOf(today);
  const taskId = params.get('task');
  const filters = {
    q: params.get('q') ?? '',
    assignee: params.get('assignee') ?? '',
    label: params.get('label') ?? '',
    priority: params.get('priority') ?? '',
    milestone: params.get('milestone') ?? '',
  };
  const setParam = (name: string, value: string | null) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === null || value === '') next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace: name !== 'task' },
    );
  };
  const open = (id: string) => {
    setParam('task', id);
  };

  const boardTasks = board === undefined ? [] : tasks.filter((t) => t.boardId === board.id);
  const shown = boardTasks.filter((t) => !t.archived && matches(t, filters));

  // A move shows at once and is undone, with the reason, if the API refuses it.
  const move = async (task: TaskSummaryDto, target: MoveTarget) => {
    setMoveError(null);
    const key = workKeys.tasks(projectId);
    const before = queryClient.getQueryData<TaskSummaryDto[]>(key);
    const column = columnTasks(tasks, target.columnId).filter((t) => t.id !== task.id);
    const category =
      board?.columns.find((c) => c.id === target.columnId)?.category ?? task.category;
    queryClient.setQueryData<TaskSummaryDto[]>(key, (current) =>
      (current ?? []).map((t) =>
        t.id === task.id
          ? {
              ...t,
              columnId: target.columnId,
              category,
              position: positionBetween(column, target.beforeTaskId),
            }
          : t,
      ),
    );
    try {
      const moved = await postJson(
        `${WORK}/tasks/${task.id}/move`,
        target,
        TaskSummarySchema,
        csrf,
        { expectedVersion: task.version },
      );
      queryClient.setQueryData<TaskSummaryDto[]>(key, (current) =>
        (current ?? []).map((t) => (t.id === moved.id ? moved : t)),
      );
      await queryClient.invalidateQueries({ queryKey: ['project-work', 'activity', projectId] });
      await queryClient.invalidateQueries({ queryKey: workKeys.task(task.id) });
      await queryClient.invalidateQueries({ queryKey: workKeys.mine });
    } catch (error) {
      queryClient.setQueryData(key, before);
      setMoveError(
        `${task.key} could not be moved: ${error instanceof Error ? errorMessage(error) : String(error)}`,
      );
      await queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const quickAdd = useWorkMutation(
    projectId,
    ({ columnId, title }: { columnId: string; title: string }) =>
      postJson(
        `${WORK}/projects/${projectId}/tasks`,
        { boardId: board?.id ?? '', columnId, title },
        TaskDetailSchema,
        csrf,
      ),
  );
  const changeDue = useWorkMutation(
    projectId,
    ({ task, dueDate }: { task: TaskSummaryDto; dueDate: string | null }) =>
      patchJson(`${WORK}/tasks/${task.id}`, { dueDate }, TaskDetailSchema, csrf, task.version),
  );

  const role =
    workspace.myRole === null ? 'Project office' : `Your role: ${ROLE_LABELS[workspace.myRole]}`;
  return (
    <Stack spacing={2.5}>
      <PageHeader
        title={`${workspace.project.code} — ${workspace.project.name}`}
        description={`${role}. Boards, tasks and milestones of this project.`}
        actions={
          <>
            {back}
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => {
                setDialog('activity');
              }}
            >
              Activity
            </Button>
            {workspace.canManage && (
              <Button
                variant="outlined"
                startIcon={<SettingsOutlinedIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('manage');
                }}
              >
                Manage
              </Button>
            )}
          </>
        }
      />
      {workspace.project.status === 'inactive' && (
        <Alert severity="info" variant="outlined">
          This project is inactive.
        </Alert>
      )}
      <Section
        title={board === undefined ? 'Boards' : board.name}
        actions={
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            useFlexGap
          >
            {boards.length > 0 && (
              <TextField
                select
                size="small"
                label="Board"
                value={board?.id ?? ''}
                sx={{ minWidth: 180 }}
                onChange={(event) => {
                  setParam('board', event.target.value);
                }}
              >
                {boards.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {workspace.canManage && (
              <Button
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('board');
                }}
              >
                New board
              </Button>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              aria-label="View"
              onChange={(_, next: View | null) => {
                if (next !== null) setParam('view', next);
              }}
            >
              <ToggleButton value="kanban">Kanban</ToggleButton>
              <ToggleButton value="list">List</ToggleButton>
              <ToggleButton value="calendar">Calendar</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      >
        <Box sx={{ p: 2 }}>
          {board === undefined ? (
            <Stack spacing={1} sx={{ py: 4, alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="subtitle1">No board yet</Typography>
              <Typography variant="body2" color="text.secondary">
                {workspace.canManage
                  ? 'Create a board to start planning the work.'
                  : 'A project manager creates the first board.'}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Filters workspace={workspace} filters={filters} onChange={setParam} />
              {moveError !== null && (
                <Alert
                  severity="error"
                  variant="outlined"
                  onClose={() => {
                    setMoveError(null);
                  }}
                >
                  {moveError}
                </Alert>
              )}
              {[quickAdd, changeDue].map(
                (m, index) =>
                  m.isError && (
                    <Alert key={index} severity="error" variant="outlined">
                      {errorMessage(m.error)}
                    </Alert>
                  ),
              )}
              {tasksError !== null && <ErrorState message={tasksError} />}
              {tasksLoading && <LoadingState label="Loading the tasks" />}
              {!tasksLoading && view === 'kanban' && (
                <Kanban
                  board={board}
                  tasks={shown}
                  workspace={workspace}
                  today={today}
                  onOpen={open}
                  onMove={(task, target) => {
                    void move(task, target);
                  }}
                  onQuickAdd={(columnId, title) => quickAdd.mutateAsync({ columnId, title })}
                />
              )}
              {!tasksLoading && view === 'list' && (
                <TaskList
                  board={board}
                  tasks={shown}
                  workspace={workspace}
                  today={today}
                  onOpen={open}
                  onStatus={(task, columnId) => {
                    void move(task, { columnId, beforeTaskId: null });
                  }}
                  onDueDate={(task, dueDate) => {
                    changeDue.mutate({ task, dueDate });
                  }}
                />
              )}
              {!tasksLoading && view === 'calendar' && (
                <TaskCalendar
                  tasks={shown}
                  month={month}
                  today={today}
                  onMonth={(next) => {
                    setParam('month', next);
                  }}
                  onOpen={open}
                />
              )}
            </Stack>
          )}
        </Box>
      </Section>
      <TaskPanel
        projectId={projectId}
        taskId={taskId}
        workspace={workspace}
        tasks={tasks}
        today={today}
        onClose={() => {
          setParam('task', null);
        }}
        onOpen={open}
      />
      {dialog === 'board' && (
        <NewBoardDialog
          projectId={projectId}
          onClose={(created) => {
            setDialog(null);
            if (created !== undefined) setParam('board', created);
          }}
        />
      )}
      {dialog === 'manage' && (
        <ManageDialog
          workspace={workspace}
          board={board}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      <ActivityDrawer
        projectId={projectId}
        open={dialog === 'activity'}
        onClose={() => {
          setDialog(null);
        }}
        onOpen={(id) => {
          setDialog(null);
          open(id);
        }}
      />
    </Stack>
  );
}

function Filters({
  workspace,
  filters,
  onChange,
}: {
  workspace: ProjectWorkspaceDto;
  filters: { q: string; assignee: string; label: string; priority: string; milestone: string };
  onChange: (name: string, value: string | null) => void;
}) {
  const select = (
    name: keyof typeof filters,
    label: string,
    options: readonly { value: string; label: string }[],
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={filters[name]}
      sx={{ minWidth: 150 }}
      onChange={(event) => {
        onChange(name, event.target.value);
      }}
    >
      <MenuItem value="">All</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
      <TextField
        size="small"
        label="Search tasks"
        value={filters.q}
        onChange={(event) => {
          onChange('q', event.target.value);
        }}
      />
      {select(
        'assignee',
        'Assignee',
        workspace.members.map((m) => ({ value: m.employeeId, label: m.name })),
      )}
      {select(
        'label',
        'Label',
        workspace.labels.map((l) => ({ value: l.id, label: l.name })),
      )}
      {select(
        'priority',
        'Priority',
        (Object.keys(PRIORITY_LABELS) as TaskPriority[]).map((p) => ({
          value: p,
          label: PRIORITY_LABELS[p],
        })),
      )}
      {select(
        'milestone',
        'Milestone',
        workspace.milestones.map((m) => ({ value: m.id, label: m.name })),
      )}
    </Stack>
  );
}

function NewBoardDialog({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: (created?: string) => void;
}) {
  const csrf = useCsrf();
  const [name, setName] = useState('');
  const create = useWorkMutation(
    projectId,
    () =>
      postJson(`${WORK}/projects/${projectId}/boards`, { name: name.trim() }, BoardSchema, csrf),
    (board) => {
      onClose(board.id);
    },
  );
  return (
    <FormDialog
      open
      title="New board"
      submitLabel="Create board"
      canSubmit={name.trim() !== ''}
      submitting={create.isPending}
      error={create.isError ? errorMessage(create.error) : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Board name"
        value={name}
        required
        autoFocus
        helperText="It starts with the columns To Do, In Progress, Review and Done, which can be changed."
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
    </FormDialog>
  );
}

function ActivityDrawer({
  projectId,
  open,
  onClose,
  onOpen,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onOpen: (taskId: string) => void;
}) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 } } } }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="subtitle1" component="h2" sx={{ flex: 1 }}>
          Project activity
        </Typography>
        <IconButton aria-label="Close the activity" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      {open && <ActivityList projectId={projectId} onOpen={onOpen} />}
    </Drawer>
  );
}

function ActivityList({
  projectId,
  onOpen,
}: {
  projectId: string;
  onOpen: (taskId: string) => void;
}) {
  const activity = useActivity(projectId);
  return (
    <Box sx={{ p: 2 }}>
      {activity.isPending && <LoadingState label="Loading the activity" />}
      {activity.isError && <ErrorState message={errorMessage(activity.error)} />}
      {activity.data?.length === 0 && (
        <Typography color="text.secondary">Nothing has happened yet.</Typography>
      )}
      <Stack spacing={1.25}>
        {(activity.data ?? []).map((entry) => (
          <Box key={entry.id}>
            <Typography variant="body2">
              <strong>{entry.actorName ?? 'System'}</strong> {entry.summary}
              {entry.taskId !== null && entry.taskKey !== null && (
                <>
                  {' '}
                  <Button
                    size="small"
                    sx={{ p: 0, minWidth: 0 }}
                    onClick={() => {
                      onOpen(entry.taskId ?? '');
                    }}
                  >
                    {entry.taskKey}
                  </Button>
                </>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.occurredAt.slice(0, 16).replace('T', ' ')}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
